import { useEffect, useRef, useState } from 'react';
import './HorizontalScroll.css';

export default function HorizontalScroll({
  children,
  fullBleed = false,
  trackRef,
  onScroll,
  backgroundColor,
  padding,
  measureItems = true,
  className = '',
  trackClassName = '',
}) {
  const internalTrackRef = useRef(null);
  const [isCentered, setIsCentered] = useState(false);
  const [itemHeight, setItemHeight] = useState(0);

  const setTrackRef = (node) => {
    internalTrackRef.current = node;
    if (typeof trackRef === 'function') {
      trackRef(node);
    } else if (trackRef && typeof trackRef === 'object') {
      trackRef.current = node;
    }
  };

  const classes = ['HorizontalScroll', fullBleed ? 'HorizontalScroll--fullBleed' : '', className]
    .filter(Boolean)
    .join(' ');
  const trackClasses = [
    'HorizontalScroll__track',
    isCentered ? 'HorizontalScroll__track--centered' : '',
    trackClassName,
  ].filter(Boolean).join(' ');
  const style = backgroundColor ? { background: backgroundColor } : undefined;
  const trackStyle = {
    ...(padding ? { padding } : {}),
    ...(itemHeight ? { '--horizontal-scroll-item-height': `${itemHeight}px` } : {}),
  };

  useEffect(() => {
    const track = internalTrackRef.current;
    if (!track) return undefined;

    const updateAlignment = () => {
      const hasOverflow = track.scrollWidth > track.clientWidth + 1;
      setIsCentered(!hasOverflow);
    };

    updateAlignment();

    let resizeObserver;
    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(updateAlignment);
      resizeObserver.observe(track);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateAlignment);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateAlignment);
      }
    };
  }, [children]);

  useEffect(() => {
    const track = internalTrackRef.current;
    if (!track || !measureItems) {
      if (!measureItems) setItemHeight(0);
      return undefined;
    }

    const getItemHeight = (item) => {
      const child = item.firstElementChild;
      const itemBoxHeight = item.getBoundingClientRect().height;
      const itemScrollHeight = item.scrollHeight || 0;
      const childBoxHeight = child ? child.getBoundingClientRect().height : 0;
      const childScrollHeight = child ? child.scrollHeight || 0 : 0;
      return Math.max(itemBoxHeight, itemScrollHeight, childBoxHeight, childScrollHeight);
    };

    const updateItemHeights = () => {
      const items = Array.from(track.children || []);
      if (!items.length) {
        setItemHeight(0);
        return;
      }
      const maxHeight = items.reduce((max, item) => Math.max(max, getItemHeight(item)), 0);
      const roundedHeight = Math.ceil(maxHeight);
      setItemHeight((prev) => (prev === roundedHeight ? prev : roundedHeight));
    };

    updateItemHeights();

    let resizeObserver;
    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(updateItemHeights);
      resizeObserver.observe(track);
      Array.from(track.children || []).forEach((child) => {
        resizeObserver.observe(child);
        if (child.firstElementChild) {
          resizeObserver.observe(child.firstElementChild);
        }
      });
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateItemHeights);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateItemHeights);
      }
    };
  }, [children, measureItems]);

  return (
    <div className={classes} style={style}>
      <div className={trackClasses} ref={setTrackRef} onScroll={onScroll} style={trackStyle}>
        {children}
      </div>
    </div>
  );
}
